import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/client';
import { useMyAddressRequests, useRequestAddressChange } from '../../api/hooks';
import type { ShopAddress } from '../../api/types';
import { useSession } from '../../auth/session';
import { Alert, Button, Chip, Field, Modal, Rail } from '../../ui';
import { latestRequest, proposedRows } from './address-request';

type Kind = 'REGISTERED' | 'BILLING' | 'SHIPPING';


/**
 * R-18 — ask to change a billing or delivery address. It does not change the address: the request goes to
 * Miraki, the approved address stays in use, and the status (pending / approved / rejected, with the
 * reason) is shown here until the retailer's next request.
 */
export function AddressEdit({ kind, address }: { kind: Kind; address: ShopAddress | null }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const requests = useMyAddressRequests();
  const send = useRequestAddressChange();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ line1: '', line2: '', city: '', postalCode: '', countryCode: '', contactName: '', contactPhone: '' });
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const may = me?.kind === 'retailer' && me.permissions.includes('shop.profile');
  const latest = latestRequest(requests.data ?? [], kind, address?.id ?? null);
  const pending = latest?.status === 'PENDING';
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const blank = (v: string) => (v.trim() === '' ? null : v.trim());
  const missing = form.line1.trim() === '' || form.city.trim() === '' || (form.countryCode.trim() !== '' && form.countryCode.trim().length !== 2);

  function begin() {
    setForm({
      line1: address?.line1 ?? '', line2: address?.line2 ?? '', city: address?.city ?? '',
      postalCode: address?.postalCode ?? '', countryCode: address?.countryCode ?? '',
      contactName: address?.contactName ?? '', contactPhone: address?.contactPhone ?? '',
    });
    setError(null); setSent(false); setOpen(true);
  }

  function submit() {
    setError(null);
    send.mutate({
      kind, ...(address ? { addressId: address.id } : {}),
      line1: form.line1.trim(), city: form.city.trim(), line2: blank(form.line2), postalCode: blank(form.postalCode),
      countryCode: blank(form.countryCode)?.toUpperCase() ?? null, contactName: blank(form.contactName), contactPhone: blank(form.contactPhone),
    }, {
      onSuccess: () => { setOpen(false); setSent(true); },
      onError: (e) => setError(e instanceof ApiError ? e.message : t('shop.account.addrFailed')),
    });
  }

  const tone = latest?.status === 'APPROVED' ? 'done' : latest?.status === 'REJECTED' ? 'stop' : 'us';
  const statusText = latest
    ? t(`shop.account.address${latest.status === 'PENDING' ? 'Pending' : latest.status === 'APPROVED' ? 'Approved' : 'Rejected'}`)
    : null;

  return (
    <div style={{ marginTop: 'var(--mk-space-3)' }}>
      {may && !pending ? (
        <Button size="sm" tone="ghost" onClick={begin}>{address ? t('shop.account.editAddress') : t('shop.account.addAddress')}</Button>
      ) : null}
      {latest ? <div style={{ marginTop: 'var(--mk-space-2)' }}><Chip tone={tone}>{statusText}</Chip></div> : null}
      {pending ? (
        <>
          <p className="mk-dim">{t('shop.account.addressPendingNote')}</p>
          <div className="mk-dim"><b>{t('shop.account.addressProposed')}</b></div>
          <Rail items={proposedRows(latest.proposed, t)} />
        </>
      ) : null}
      {latest?.status === 'REJECTED' ? (
        <p className="mk-dim">{t('shop.account.addressRejectedNote', { reason: latest.decisionReason ?? '' })}</p>
      ) : null}
      {sent ? <Alert tone="us">{t('shop.account.addrSent')}</Alert> : null}

      {open ? (
        <Modal
          title={t('shop.account.addressEditTitle')}
          onClose={() => { if (!send.isPending) setOpen(false); }}
          footer={(
            <>
              <Button tone="ghost" disabled={send.isPending} onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
              <Button disabled={missing || send.isPending} onClick={submit}>
                {send.isPending ? t('shop.account.addrSending') : t('shop.account.addrSubmit')}
              </Button>
            </>
          )}
        >
          <p>{t('shop.account.addressEditIntro')}</p>
          <Field label={t('shop.account.addrContact')}><input className="mk-fld" maxLength={120} value={form.contactName} onChange={(e) => set('contactName', e.target.value)} /></Field>
          <Field label={t('shop.account.addrLine1')}><input className="mk-fld" maxLength={200} value={form.line1} onChange={(e) => set('line1', e.target.value)} /></Field>
          <Field label={t('shop.account.addrLine2')}><input className="mk-fld" maxLength={200} value={form.line2} onChange={(e) => set('line2', e.target.value)} /></Field>
          <Field label={t('shop.account.addrCity')}><input className="mk-fld" maxLength={100} value={form.city} onChange={(e) => set('city', e.target.value)} /></Field>
          <Field label={t('shop.account.addrPostal')}><input className="mk-fld" maxLength={20} value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} /></Field>
          <Field label={t('shop.account.addrCountry')} hint={t('shop.account.addrCountryHint')}><input className="mk-fld" maxLength={2} value={form.countryCode} onChange={(e) => set('countryCode', e.target.value)} /></Field>
          <Field label={t('shop.account.addrPhone')}><input className="mk-fld" maxLength={40} value={form.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} /></Field>
          {error ? <div role="alert" className="mk-dim">{error}</div> : null}
        </Modal>
      ) : null}
    </div>
  );
}
